import React, { useState } from "react";
import styled from "styled-components";
import { useGetAppealsQuery, useUpdateAppealStatusMutation } from "../store/api";
import { useNavigate } from "react-router-dom";

const AppealsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FiltersCard = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: var(--bg-card);

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const AppealsCard = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableHeader = styled.thead`
  background: var(--bg-primary);
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--border-divider);
  cursor: pointer;

  &:hover {
    background: var(--bg-primary);
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  background: ${(props) => {
    switch (props.status?.toUpperCase()) {
      case "PENDING":
        return "var(--status-pending-bg)";
      case "IN_PROGRESS":
        return "var(--status-processing-bg)";
      case "APPROVED":
        return "var(--status-success-bg)";
      case "REJECTED":
        return "var(--status-error-bg)";
      case "SUPPORT_REVIEW":
        return "var(--alert-info-bg)";
      default:
        return "var(--bg-secondary)";
    }
  }};
  color: ${(props) => {
    switch (props.status?.toUpperCase()) {
      case "PENDING":
        return "var(--status-pending-text)";
      case "IN_PROGRESS":
        return "var(--status-processing-text)";
      case "APPROVED":
        return "var(--status-success-text)";
      case "REJECTED":
        return "var(--status-error-text)";
      case "SUPPORT_REVIEW":
        return "var(--alert-info-text)";
      default:
        return "var(--text-secondary)";
    }
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
`;

const Subtitle = styled.p`
  margin: 0.5rem 0 0 0;
  color: var(--text-muted);
  font-size: 1rem;
`;

// Action buttons
const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{ variant: "approve" | "reject" }>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  background: ${(props) =>
    props.variant === "approve" ? "var(--accent-success)" : "var(--accent-error)"};
  color: #ffffff;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

/* ====== Mobile Appeal Cards ====== */
const MobileAppealsList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem;
  }
`;

const MobileAppealCard = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-divider);
  border-radius: 0.75rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-primary);
    box-shadow: 0 2px 8px var(--accent-primary-light);
  }
`;

const MobileAppealHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const MobileAppealId = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const MobileAppealBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
`;

const MobileAppealField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const MobileAppealLabel = styled.span`
  font-size: 0.6875rem;
  color: var(--text-muted);
  font-weight: 500;
  text-transform: uppercase;
`;

const MobileAppealValue = styled.span`
  font-size: 0.8125rem;
  color: var(--text-primary);
  font-weight: 600;
`;

const MobileAppealFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-divider);
  gap: 0.5rem;
  flex-wrap: wrap;
`;

function Appeals() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("all");
  const [orderFilter, setOrderFilter] = useState("");
  const [page] = useState(1);

  const {
    data: appealsResponse,
    isLoading,
    error,
    refetch,
  } = useGetAppealsQuery({
    page,
    limit: 20,
    status: statusFilter === "all" ? undefined : statusFilter,
    orderId: orderFilter || undefined,
  }, {
    pollingInterval: 10000,
    refetchOnMountOrArgChange: true,
  });

  const [updateAppealStatus, { isLoading: isUpdating }] = useUpdateAppealStatusMutation();

  const appeals = appealsResponse?.data || [];
  const totalAppeals = appealsResponse?.meta?.total || 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusText = (status: string) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "На рассмотрении";
      case "IN_PROGRESS":
        return "В обработке";
      case "APPROVED":
        return "Одобрена";
      case "REJECTED":
        return "Отклонена";
      case "SUPPORT_REVIEW":
        return "На рассмотрении саппорта";
      default:
        return status;
    }
  };

  const handleAppealClick = (appealId: string) => {
    navigate(`/appeals/${appealId}`);
  };

  const handleApprove = async (e: React.MouseEvent, appealId: string) => {
    e.stopPropagation();
    if (window.confirm("Вы уверены, что хотите одобрить апелляцию? Это означает, что платеж был получен.")) {
      try {
        await updateAppealStatus({ id: appealId, status: "APPROVED" }).unwrap();
        refetch();
      } catch (err: any) {
        console.error("Ошибка одобрения апелляции:", err);
        alert(err?.data?.message || "Не удалось одобрить апелляцию");
      }
    }
  };

  const handleReject = async (e: React.MouseEvent, appealId: string) => {
    e.stopPropagation();
    if (window.confirm("Вы уверены, что хотите отклонить апелляцию?")) {
      try {
        await updateAppealStatus({ id: appealId, status: "REJECTED" }).unwrap();
        refetch();
      } catch (err: any) {
        console.error("Ошибка отклонения апелляции:", err);
        alert(err?.data?.message || "Не удалось отклонить апелляцию");
      }
    }
  };

  if (isLoading) {
    return (
      <AppealsContainer>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          Загрузка апелляций...
        </div>
      </AppealsContainer>
    );
  }

  if (error) {
    return (
      <AppealsContainer>
        <div style={{ textAlign: "center", padding: "2rem", color: "var(--accent-error)" }}>
          Ошибка загрузки апелляций
        </div>
      </AppealsContainer>
    );
  }

  const canAct = (status: string) => {
    const s = status?.toUpperCase();
    return s === "PENDING" || s === "IN_PROGRESS";
  };

  return (
    <AppealsContainer>
      <Header>
        <Subtitle>
          Управление апелляциями по ордерам. Всего: {totalAppeals}
        </Subtitle>
      </Header>

      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Статус</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Все статусы</option>
              <option value="PENDING">На рассмотрении</option>
              <option value="APPROVED">Одобрена</option>
              <option value="REJECTED">Отклонена</option>
              <option value="SUPPORT_REVIEW">На рассмотрении саппорта</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>ID Ордера</Label>
            <Input
              type="text"
              placeholder="Поиск по ID ордера"
              value={orderFilter}
              onChange={(e) => setOrderFilter(e.target.value)}
            />
          </FilterGroup>
        </FiltersGrid>
      </FiltersCard>

      <AppealsCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>ID Апелляции</TableHeaderCell>
              <TableHeaderCell>Создано</TableHeaderCell>
              <TableHeaderCell>Номер транзакции</TableHeaderCell>
              <TableHeaderCell>Реквизит</TableHeaderCell>
              <TableHeaderCell>Сумма (RUB)</TableHeaderCell>
              <TableHeaderCell>Причина</TableHeaderCell>
              <TableHeaderCell>Статус</TableHeaderCell>
              <TableHeaderCell>Действие</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <tbody>
            {appeals.length === 0 ? (
              <tr>
                <TableCell
                  colSpan={8}
                  style={{ textAlign: "center", padding: "2rem" }}
                >
                  <EmptyState>
                    Нет апелляций
                  </EmptyState>
                </TableCell>
              </tr>
            ) : (
              appeals.map((appeal: any) => (
                <TableRow
                  key={appeal.id}
                  onClick={() => handleAppealClick(appeal.id)}
                >
                  <TableCell>#{appeal.id.substring(0, 8)}</TableCell>
                  <TableCell>{formatDate(appeal.createdAt)}</TableCell>
                  <TableCell>{appeal.order?.invId || appeal.orderId}</TableCell>
                  <TableCell>
                    {appeal.order?.requisite?.cardNumber ||
                      appeal.order?.requisite?.phoneNumber ||
                      appeal.order?.requisite?.sbpPhoneNumber ||
                      appeal.order?.cardNumber ||
                      appeal.order?.phoneNumber ||
                      "—"}
                  </TableCell>
                  <TableCell>
                    {appeal.order?.amount ? `${appeal.order.amount} ₽` : "—"}
                  </TableCell>
                  <TableCell>{appeal.reason}</TableCell>
                  <TableCell>
                    <StatusBadge status={appeal.status}>
                      {getStatusText(appeal.status)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    {canAct(appeal.status) ? (
                      <ActionButtonsContainer>
                        <ActionButton
                          variant="approve"
                          onClick={(e) => handleApprove(e, appeal.id)}
                          disabled={isUpdating}
                        >
                          ✓ Одобрить
                        </ActionButton>
                        <ActionButton
                          variant="reject"
                          onClick={(e) => handleReject(e, appeal.id)}
                          disabled={isUpdating}
                        >
                          ✗ Отклонить
                        </ActionButton>
                      </ActionButtonsContainer>
                    ) : (
                      <span style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>
                        {appeal.status?.toUpperCase() === "APPROVED" ? "Одобрена" :
                         appeal.status?.toUpperCase() === "REJECTED" ? "Отклонена" :
                         "Ожидает решения"}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </tbody>
        </Table>

        {/* Mobile Appeal Cards */}
        <MobileAppealsList>
          {appeals.length === 0 ? (
            <EmptyState>Нет апелляций</EmptyState>
          ) : (
            appeals.map((appeal: any) => (
              <MobileAppealCard key={appeal.id} onClick={() => handleAppealClick(appeal.id)}>
                <MobileAppealHeader>
                  <MobileAppealId>#{appeal.id.substring(0, 8)}</MobileAppealId>
                  <StatusBadge status={appeal.status}>
                    {getStatusText(appeal.status)}
                  </StatusBadge>
                </MobileAppealHeader>
                <MobileAppealBody>
                  <MobileAppealField>
                    <MobileAppealLabel>Транзакция</MobileAppealLabel>
                    <MobileAppealValue>{appeal.order?.invId || appeal.orderId}</MobileAppealValue>
                  </MobileAppealField>
                  <MobileAppealField>
                    <MobileAppealLabel>Сумма</MobileAppealLabel>
                    <MobileAppealValue>
                      {appeal.order?.amount ? `${appeal.order.amount} ₽` : "—"}
                    </MobileAppealValue>
                  </MobileAppealField>
                  <MobileAppealField style={{ gridColumn: '1 / -1' }}>
                    <MobileAppealLabel>Причина</MobileAppealLabel>
                    <MobileAppealValue style={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      {appeal.reason}
                    </MobileAppealValue>
                  </MobileAppealField>
                </MobileAppealBody>
                <MobileAppealFooter>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {formatDate(appeal.createdAt)}
                  </span>
                  {canAct(appeal.status) && (
                    <ActionButtonsContainer>
                      <ActionButton
                        variant="approve"
                        onClick={(e) => handleApprove(e, appeal.id)}
                        disabled={isUpdating}
                      >
                        ✓
                      </ActionButton>
                      <ActionButton
                        variant="reject"
                        onClick={(e) => handleReject(e, appeal.id)}
                        disabled={isUpdating}
                      >
                        ✗
                      </ActionButton>
                    </ActionButtonsContainer>
                  )}
                </MobileAppealFooter>
              </MobileAppealCard>
            ))
          )}
        </MobileAppealsList>
      </AppealsCard>
    </AppealsContainer>
  );
}

export default Appeals;
